// @ts-nocheck
// ============================================
// Main Zustand Store - Using Slices Pattern
// ============================================
// All data is now stored in Firebase Firestore.
// No localStorage persistence - all data synced with cloud.
// ============================================

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { nanoid } from 'nanoid';
import { 
  App, 
  AppVersion, 
  Screen, 
  AnalyticsEvent, 
  User, 
  HistoryRecord, 
  VersionDiff, 
  DEFAULT_EVENT_FILTERS,
  EventFilters,
  SearchResultItem
} from '../types';

// Import slices
import { createAuthSlice, AuthState } from './slices/authSlice';
import { createFirebaseSlice, FirebaseState } from './slices/firebaseSlice';
import { createAppSlice, AppState } from './slices/appSlice';
import { createEventsSlice, EventsState } from './slices/eventsSlice';
import { createCanvasSlice, CanvasState } from './slices/canvasSlice';
import { createFiltersSlice, FiltersState } from './slices/filtersSlice';
import { createHistorySlice, HistoryState, AppStateSnapshot } from './slices/historySlice';
import { createUISlice, UIState } from './slices/uiSlice';

// Import Firebase service for Cloud Function calls
import { importProject as importProjectToFirestore } from '../lib/firebase/projectService';

// Type for the main store state and actions
export type AppStore = AuthState 
  & FirebaseState 
  & AppState 
  & EventsState 
  & CanvasState 
  & FiltersState 
  & HistoryState 
  & UIState
  & {
    // Additional combined state
    history: HistoryRecord[];
    addHistoryRecord: (record: Omit<HistoryRecord, 'id' | 'timestamp'>) => void;
    
    // Export/Import
    exportData: () => Promise<string>;
    exportDataWithScope: (scope: 'all' | 'app' | 'version' | 'screen') => Promise<string>;
    importData: (jsonData: string) => Promise<{ success: boolean; error?: string }>;
    importDataWithScope: (jsonData: string, scope: 'all' | 'app' | 'version' | 'screen', mode: 'merge' | 'replace') => Promise<{ success: boolean; error?: string }>;
    
    // Firebase Code Generation
    generateFirebaseCode: (eventId: string) => string;
    
    // Version Comparison
    compareVersions: (baseVersionId: string, targetVersionId: string) => VersionDiff | null;
  };

// Maximum number of undo states to keep
const MAX_UNDO_HISTORY = 50;

// Generate unique IDs using nanoid
const generateId = () => nanoid();

// ============================================
// Утилиты для работы с eventCount
// ============================================

// Подсчитать количество событий в версии
const calculateEventCount = (version: AppVersion): number => {
  if (!version.screens) return 0;
  return version.screens.reduce((total, screen) => {
    return total + (screen.events?.length || 0);
  }, 0);
};

// Добавить eventCount к версии (миграция для существующих данных)
const addEventCountToVersion = (version: AppVersion): AppVersion => {
  if (version.eventCount === undefined) {
    return {
      ...version,
      eventCount: calculateEventCount(version)
    };
  }
  return version;
};

// Helper to convert image URL to base64 - shared between export functions
const convertImageToBase64 = async (imageUrl: string): Promise<string> => {
  if (imageUrl.startsWith('data:')) {
    return imageUrl;
  }
  
  if (!imageUrl || !imageUrl.startsWith('http')) {
    try {
      const fullUrl = imageUrl.startsWith('/') 
        ? window.location.origin + imageUrl 
        : window.location.origin + '/' + imageUrl;
      const response = await fetch(fullUrl);
      if (!response.ok) return imageUrl;
      const blob = await response.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(blob);
      });
    } catch {
      return imageUrl;
    }
  }
  
  try {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(blob);
    });
  } catch {
    return imageUrl;
  }
};

// ============================================
// Create the main store using slices pattern
// ============================================

// Define additional store parts that aren't in slices
const additionalStore = (set: any, get: any): Partial<AppStore> => ({
  // History records (separate from undo/redo)
  history: [],
  
  addHistoryRecord: (record: Omit<HistoryRecord, 'id' | 'timestamp'>) => {
    const newRecord: HistoryRecord = {
      ...record,
      id: generateId(),
      timestamp: new Date().toISOString()
    };
    
    set((state: any) => ({
      history: [newRecord, ...(state.history || [])].slice(0, 100)
    }));
  },
  
  // Export data to JSON (with images as base64)
  exportData: async () => {
    const { apps } = get();
    
    const appsWithImages = await Promise.all(
      apps.map(async (app: App) => ({
        ...app,
        versions: await Promise.all(
          app.versions.map(async (version: AppVersion) => {
            const { name, ...versionWithoutName } = version;
            return addEventCountToVersion({
              ...versionWithoutName,
              screens: await Promise.all(
                version.screens.map(async (screen: Screen) => ({
                  ...screen,
                  imageUrl: screen.imageUrl ? await convertImageToBase64(screen.imageUrl) : '',
                }))
              ),
            });
          })
        ),
      }))
    );
    
    const exportObj = {
      apps: appsWithImages,
      exportedAt: new Date().toISOString()
    };
    return JSON.stringify(exportObj, null, 2);
  },
  
  // Export data with specific scope
  exportDataWithScope: async (scope: 'all' | 'app' | 'version' | 'screen') => {
    const { apps, currentApp, currentVersion, currentScreen } = get();
    
    let exportObj: any = {
      scope,
      exportedAt: new Date().toISOString()
    };
    
    if (scope === 'all') {
      const appsWithImages = await Promise.all(
        apps.map(async (app: App) => ({
          ...app,
          versions: await Promise.all(
            app.versions.map(async (version: AppVersion) => {
              const { name, ...versionWithoutName } = version;
              return addEventCountToVersion({
                ...versionWithoutName,
                screens: await Promise.all(
                  version.screens.map(async (screen: Screen) => ({
                    ...screen,
                    imageUrl: screen.imageUrl ? await convertImageToBase64(screen.imageUrl) : '',
                  }))
                ),
              });
            })
          ),
        }))
      );
      exportObj.apps = appsWithImages;
    } else if (scope === 'app' && currentApp) {
      const appWithImages = {
        ...currentApp,
        versions: await Promise.all(
          currentApp.versions.map(async (version: AppVersion) => {
            const { name, ...versionWithoutName } = version;
            return addEventCountToVersion({
              ...versionWithoutName,
              screens: await Promise.all(
                version.screens.map(async (screen: Screen) => ({
                  ...screen,
                  imageUrl: screen.imageUrl ? await convertImageToBase64(screen.imageUrl) : '',
                }))
              ),
            });
          })
        ),
      };
      exportObj.app = appWithImages;
    } else if (scope === 'version' && currentApp && currentVersion) {
      const { name, ...versionWithoutName } = currentVersion;
      const versionWithImages = {
        ...versionWithoutName,
        screens: await Promise.all(
          currentVersion.screens.map(async (screen: Screen) => ({
            ...screen,
            imageUrl: screen.imageUrl ? await convertImageToBase64(screen.imageUrl) : '',
          }))
        ),
      };
      exportObj.version = versionWithImages;
      exportObj.appName = currentApp.name;
    } else if (scope === 'screen' && currentApp && currentVersion && currentScreen) {
      const screenWithImage = {
        ...currentScreen,
        imageUrl: currentScreen.imageUrl ? await convertImageToBase64(currentScreen.imageUrl) : '',
      };
      exportObj.screen = screenWithImage;
      exportObj.appName = currentApp.name;
      exportObj.versionName = currentVersion.name;
    }
    
    return JSON.stringify(exportObj, null, 2);
  },
  
  // Import data from JSON - saves to Firestore via Cloud Function
  importData: async (jsonData: string) => {
    try {
      const data = JSON.parse(jsonData);
      
      if (!data.apps || !Array.isArray(data.apps)) {
        console.error('Invalid import data: missing apps array');
        return { success: false, error: 'Невірний формат даних: відсутній масив apps' };
      }
      
      const { user } = get();
      
      // ONLY save to Firebase, do NOT update local state - sync will get real data
      const importedAppIds: string[] = [];
      
      for (const app of data.apps) {
        const processedApp = {
          ...app,
          versions: (app.versions || []).map((version: any) =>
            addEventCountToVersion({
              ...version,
              screens: (version.screens || []).map((screen: any) => ({
                ...screen,
                imageUrl: screen.imageUrl || '',
              })),
            })
          ),
        };
        
        // Сохраняем в Firestore через Cloud Function
        if (user?.id) {
          try {
            const result = await importProjectToFirestore({
              name: processedApp.name,
              description: processedApp.description,
              versions: processedApp.versions,
              isDemo: processedApp.isDemo || false,
            });
            
            if (result.success && result.appId) {
              importedAppIds.push(result.appId);
              console.log('[importData] Project saved to Firestore:', result.appId);
            }
          } catch (firestoreError) {
            console.error('[importData] Failed to save to Firestore:', firestoreError);
          }
        }
      }
      
      // Sync with Firebase to get the real data
      // This will update local state with data from Firebase
      if (importedAppIds.length > 0 && user?.id) {
        const syncFn = get().syncWithFirebase;
        if (syncFn) {
          await syncFn();
        }
      }
      
      return { success: true };
    } catch (error: any) {
      console.error('Error importing data:', error);
      return { success: false, error: `Помилка імпорту: ${error.message}` };
    }
  },
  
  // Import data with specific scope
  importDataWithScope: async (jsonData: string, scope: 'all' | 'app' | 'version' | 'screen', mode: 'merge' | 'replace') => {
    try {
      const data = JSON.parse(jsonData);
      const { apps, currentApp, currentVersion } = get();
      
      if (scope === 'all') {
        if (!data.apps && !data.app) {
          return { success: false, error: 'Невірний формат даних: відсутній масив apps' };
        }
        
        const importedApps = data.apps || [data.app];
        const user = (get() as any).user;
        
        // ONLY save to Firebase, do NOT update local state - sync will get real data
        const importedAppIds: string[] = [];
        
        for (const app of importedApps) {
          const processedApp = {
            ...app,
            versions: (app.versions || []).map((version: any) => {
              const { name, ...versionWithoutName } = version;
              return addEventCountToVersion({
                ...versionWithoutName,
                id: generateId(),
                screens: (version.screens || []).map((screen: any) => ({
                  ...screen,
                  id: generateId(),
                  events: (screen.events || []).map((event: any) => ({
                    ...event,
                    id: generateId()
                  }))
                })),
              });
            }),
          };
          
          if (user?.id) {
            try {
              const result = await importProjectToFirestore({
                name: mode === 'replace' ? processedApp.name : `${processedApp.name} (імпорт)`,
                description: processedApp.description,
                versions: processedApp.versions,
                isDemo: processedApp.isDemo || false,
              });
              
              if (result.success && result.appId) {
                importedAppIds.push(result.appId);
              }
            } catch (firestoreError) {
              console.error('[importDataWithScope] Failed to save to Firestore:', firestoreError);
            }
          }
        }
        
        // Sync with Firebase to get the real data
        // This will update local state with data from Firebase
        if (importedAppIds.length > 0 && user?.id) {
          const syncFn = get().syncWithFirebase;
          if (syncFn) {
            await syncFn();
          }
        }
        
        return { success: true };
      }
      
      if (scope === 'app') {
        if (!data.app && !data.apps) {
          return { success: false, error: 'Невірний формат даних: відсутній об\'єкт app' };
        }
        
        const importedApp = data.app || data.apps?.[0];
        const user = (get() as any).user;
        
        // ONLY save to Firebase, do NOT update local state - sync will get real data
        if (user?.id && currentApp) {
          try {
            const processedVersions = (importedApp.versions || []).map((version: any) => {
              const { name, ...versionWithoutName } = version;
              return addEventCountToVersion({
                ...versionWithoutName,
                id: generateId(),
                screens: (version.screens || []).map((screen: any) => ({
                  ...screen,
                  id: generateId(),
                  events: (screen.events || []).map((event: any) => ({
                    ...event,
                    id: generateId()
                  }))
                })),
              });
            });
            
            const result = await importProjectToFirestore({
              name: mode === 'replace' ? currentApp.name : `${importedApp.name} (імпорт)`,
              description: importedApp.description || currentApp.description || '',
              versions: processedVersions,
              isDemo: importedApp.isDemo || false,
            });
            
            if (result.success) {
              console.log('[importDataWithScope] App imported to Firestore');
              
              // Sync with Firebase to get the real data
              const syncFn = get().syncWithFirebase;
              if (syncFn) {
                await syncFn();
              }
            }
          } catch (firestoreError) {
            console.error('[importDataWithScope] Failed to save app to Firestore:', firestoreError);
          }
        }
        
        return { success: true };
      }
      
      if (scope === 'version') {
        if (!data.version) {
          return { success: false, error: 'Невірний формат даних: відсутній об\'єкт version' };
        }
        
        if (!currentApp) {
          return { success: false, error: 'Немає активного додатку' };
        }
        
        const user = (get() as any).user;
        const importedVersion = data.version;
        
        if (mode === 'replace') {
          // REPLACE mode - replace current version with imported data
          // ONLY save to Firebase, do NOT update local state - sync will get real data
          const importedVersionData = data.version;
          const { name: importedName, ...versionWithoutName } = importedVersionData;
          const processedVersion = 
            addEventCountToVersion({
              ...versionWithoutName,
              id: currentVersion?.id || generateId(),
              screens: (importedVersionData.screens || []).map((screen: any) => ({
                ...screen,
                id: generateId(),
                events: (screen.events || []).map((event: any) => ({
                  ...event,
                  id: generateId()
                }))
              })),
            });
          
          // Save to Firebase - update the version in Firestore
          const updateVersionFn = (get() as any).updateVersion;
          if (updateVersionFn && user?.id) {
            try {
              await updateVersionFn(processedVersion.id, { version: processedVersion.version, name: processedVersion.name });
              console.log('[importDataWithScope] Version updated in Firebase');
              
              // Also update screens and events in Firebase
              // For now, just sync to get the data
              const syncFn = get().syncWithFirebase;
              if (syncFn) {
                await syncFn();
              }
            } catch (versionError) {
              console.error('[importDataWithScope] Failed to update version in Firebase:', versionError);
            }
          }
          
          return { success: true };
        } else {
          // MERGE mode - add new version
          const user = (get() as any).user;
          const importedVersionData = data.version;
          const { name: importedName, ...versionWithoutName } = importedVersionData;
          const processedVersion = 
            addEventCountToVersion({
              ...versionWithoutName,
              id: generateId(),
              // При импорте создаем имя на основе номера версии
              name: `${versionWithoutName.version || '1.0.0'} (імпорт)`,
              screens: (importedVersionData.screens || []).map((screen: any) => ({
                ...screen,
                id: generateId(),
                events: (screen.events || []).map((event: any) => ({
                  ...event,
                  id: generateId()
                }))
              })),
            });
          
          // Save to Firebase - add the imported version to Firestore
          // Use importProject to properly import all screens and events
          // ONLY save to Firebase, do NOT update local state - sync will get real data
          if (user?.id) {
            try {
              // Import the version as a new project via Cloud Function
              // This properly imports all screens and events
              const importResult = await importProjectToFirestore({
                name: currentApp.name,
                description: currentApp.description || '',
                versions: [processedVersion],
                isDemo: currentApp.isDemo || false
              });
              
              if (importResult.success && importResult.appId) {
                console.log('[importDataWithScope] Version imported via Cloud Function:', importResult.appId);
                
                // Sync with Firebase to get the imported data
                // This will update local state with REAL data from Firebase
                const syncFn = get().syncWithFirebase;
                if (syncFn) {
                  await syncFn();
                }
              } else {
                console.error('[importDataWithScope] Failed to import version:', importResult.error);
              }
            } catch (importError) {
              console.error('[importDataWithScope] Error importing version:', importError);
            }
          }
          
          return { success: true };
        }
      }
      
      if (scope === 'screen') {
        if (!data.screen) {
          return { success: false, error: 'Невірний формат даних: відсутній об\'єкт screen' };
        }
        
        if (!currentApp || !currentVersion) {
          return { success: false, error: 'Немає активної версії' };
        }
        
        const importedScreen = data.screen;
        const currentScreen = get().currentScreen;
        const user = (get() as any).user;
        
        if (mode === 'replace') {
          // REPLACE mode - replace current screen with imported data
          // ONLY save to Firebase, do NOT update local state - sync will get real data
          const processedScreen = {
            ...importedScreen,
            id: currentScreen?.id || generateId(),
            events: (importedScreen.events || []).map((event: any) => ({
              ...event,
              id: generateId()
            })),
          };
          
          // First, update the screen in Firebase
          const updateScreenFn = (get() as any).updateScreen;
          const currentVersionId = (get() as any).currentVersionId;
          if (updateScreenFn && user?.id && currentVersionId) {
            try {
              await updateScreenFn(currentApp.id, currentVersionId, processedScreen.id, { 
                name: processedScreen.name, 
                displayName: processedScreen.displayName, 
                imageUrl: processedScreen.imageUrl 
              });
              console.log('[importDataWithScope] Screen updated in Firebase');
              
              // Now add events to Firebase using the REAL screen ID
              if (processedScreen.events && processedScreen.events.length > 0) {
                console.log('[importDataWithScope] Saving events to Firebase:', processedScreen.events.length);
                const addEventFn = (get() as any).addEvent;
                if (addEventFn && currentApp && currentVersion) {
                  for (const event of processedScreen.events) {
                    try {
                      await addEventFn(processedScreen.id, {
                        name: event.name,
                        description: event.description,
                        x: event.x,
                        y: event.y,
                        type: event.type || 'click',
                        params: event.params || [],
                        status: event.status || 'new'
                      });
                      console.log('[importDataWithScope] Event saved:', event.name);
                    } catch (eventError) {
                      console.error('[importDataWithScope] Failed to save event:', event.name, eventError);
                    }
                  }
                }
              }
              
              // Sync with Firebase to get the real data
              const syncFn = get().syncWithFirebase;
              if (syncFn) {
                await syncFn();
              }
            } catch (screenError) {
              console.error('[importDataWithScope] Failed to update screen in Firebase:', screenError);
            }
          }
          
          return { success: true };
        } else {
          // MERGE mode - add new screen
          // ONLY save to Firebase, do NOT update local state - sync will get real data
          const processedScreen = {
            ...importedScreen,
            id: generateId(),
            name: `${importedScreen.name}_imported`,
            displayName: `${importedScreen.displayName} (імпорт)`,
            events: (importedScreen.events || []).map((event: any) => ({
              ...event,
              id: generateId()
            })),
          };
          
          // First, save to Firebase - add the imported screen
          const addScreenFn = (get() as any).addScreen;
          let newScreenId: string | null = null;
          
          if (addScreenFn && user?.id) {
            try {
              // Create screen in Firebase first - this returns the real Firebase screen ID
              newScreenId = await addScreenFn({
                name: processedScreen.name,
                displayName: processedScreen.displayName,
                imageUrl: processedScreen.imageUrl
              });
              console.log('[importDataWithScope] Screen saved to Firebase, new ID:', newScreenId);
              
              // Now add events to Firebase using the REAL Firebase screen ID
              if (newScreenId && processedScreen.events && processedScreen.events.length > 0) {
                console.log('[importDataWithScope] Saving events to Firebase:', processedScreen.events.length);
                const addEventFn = (get() as any).addEvent;
                if (addEventFn && currentApp && currentVersion) {
                  for (const event of processedScreen.events) {
                    try {
                      await addEventFn(newScreenId, {
                        name: event.name,
                        description: event.description,
                        x: event.x,
                        y: event.y,
                        type: event.type || 'click',
                        params: event.params || [],
                        status: event.status || 'new'
                      });
                      console.log('[importDataWithScope] Event saved:', event.name);
                    } catch (eventError) {
                      console.error('[importDataWithScope] Failed to save event:', event.name, eventError);
                    }
                  }
                }
              }
              
              // Sync with Firebase to get the real data
              const syncFn = get().syncWithFirebase;
              if (syncFn) {
                await syncFn();
              }
            } catch (screenError) {
              console.error('[importDataWithScope] Failed to save screen to Firebase:', screenError);
            }
          }
          
          return { success: true };
        }
      }
      
      return { success: false, error: 'Невідомий scope' };
    } catch (error: any) {
      console.error('Error importing data:', error);
      return { success: false, error: `Помилка імпорту: ${error.message}` };
    }
  },
  
  // Generate Firebase Analytics code
  generateFirebaseCode: (eventId: string) => {
    const { currentScreen } = get();
    if (!currentScreen) return '';
    
    const event = currentScreen.events?.find((e: AnalyticsEvent) => e.id === eventId);
    if (!event) return '';
    
    const buildDartParams = () => {
      if (event.params.length === 0) return '';
      
      const params = event.params.map(p => {
        let value: string;
        if (typeof p.value === 'string') {
          value = `'${p.value}'`;
        } else if (typeof p.value === 'boolean') {
          value = p.value ? 'true' : 'false';
        } else {
          value = String(p.value);
        }
        return `    '${p.key}': ${value},`;
      }).join('\n');
      
      return ` {\n${params}\n  }`;
    };
    
    const header = `// Event: ${event.name}
// Screen: ${currentScreen.name}
// Description: ${event.description || ''}`;
    
    const paramsStr = buildDartParams();
    const code = `${header}
logEvent(
  name: '${event.name}',${paramsStr ? `\n  parameters:${paramsStr},` : ''}
);`;
    
    return code;
  },
  
  // Compare two versions and return diff
  compareVersions: (baseVersionId: string, targetVersionId: string): VersionDiff | null => {
    const { currentApp } = get();
    if (!currentApp) return null;
    
    const baseVersion = currentApp.versions.find(v => v.id === baseVersionId);
    const targetVersion = currentApp.versions.find(v => v.id === targetVersionId);
    
    if (!baseVersion || !targetVersion) return null;
    
    const getEventKey = (eventName: string, screenName: string) => `${screenName}:${eventName}`;
    
    const paramsEqual = (params1: any, params2: any): boolean => {
      if (params1.length !== params2.length) return false;
      const sorted1 = [...params1].sort((a: any, b: any) => a.key.localeCompare(b.key));
      const sorted2 = [...params2].sort((a: any, b: any) => a.key.localeCompare(b.key));
      return sorted1.every((p1: any, i: number) => {
        const p2 = sorted2[i];
        return p1.key === p2.key && p1.value === p2.value && p1.type === p2.type;
      });
    };
    
    const detectChanges = (oldEvent: AnalyticsEvent, newEvent: AnalyticsEvent) => {
      const changes: any[] = [];
      
      if (oldEvent.type !== newEvent.type) {
        changes.push({ field: 'type', oldValue: oldEvent.type, newValue: newEvent.type });
      }
      
      if (oldEvent.description !== newEvent.description) {
        changes.push({ field: 'description', oldValue: oldEvent.description, newValue: newEvent.description });
      }
      
      if (!paramsEqual(oldEvent.params, newEvent.params)) {
        changes.push({ field: 'params', oldValue: oldEvent.params, newValue: newEvent.params });
      }
      
      const xDiff = Math.abs(oldEvent.x - newEvent.x);
      const yDiff = Math.abs(oldEvent.y - newEvent.y);
      if (xDiff > 5 || yDiff > 5) {
        changes.push({ 
          field: 'position', 
          oldValue: { x: oldEvent.x, y: oldEvent.y }, 
          newValue: { x: newEvent.x, y: newEvent.y } 
        });
      }
      
      return changes;
    };
    
    const baseEventsMap = new Map<string, any>();
    const targetEventsMap = new Map<string, any>();
    
    baseVersion.screens.forEach(screen => {
      screen.events.forEach(event => {
        baseEventsMap.set(getEventKey(event.name, screen.name), {
          event,
          screenName: screen.name,
          screenDisplayName: screen.displayName
        });
      });
    });
    
    targetVersion.screens.forEach(screen => {
      screen.events.forEach(event => {
        targetEventsMap.set(getEventKey(event.name, screen.name), {
          event,
          screenName: screen.name,
          screenDisplayName: screen.displayName
        });
      });
    });
    
    const addedEvents: any[] = [];
    const removedEvents: any[] = [];
    const changedEvents: any[] = [];
    const unchangedEvents: any[] = [];
    
    const pendingEvents: any[] = [];
    const inProgressEvents: any[] = [];
    const resolvedEvents: any[] = [];
    const rejectedEvents: any[] = [];
    
    targetEventsMap.forEach((data, key) => {
      const baseData = baseEventsMap.get(key);
      
      if (!baseData) {
        addedEvents.push({
          screenName: data.screenName,
          screenDisplayName: data.screenDisplayName,
          event: data.event
        });
        
        if (data.event.status === 'pending') {
          pendingEvents.push({ screenName: data.screenName, screenDisplayName: data.screenDisplayName, event: data.event });
        } else if (data.event.status === 'in_progress') {
          inProgressEvents.push({ screenName: data.screenName, screenDisplayName: data.screenDisplayName, event: data.event });
        } else if (data.event.status === 'resolved') {
          resolvedEvents.push({ screenName: data.screenName, screenDisplayName: data.screenDisplayName, event: data.event });
        } else if (data.event.status === 'rejected') {
          rejectedEvents.push({ screenName: data.screenName, screenDisplayName: data.screenDisplayName, event: data.event });
        }
      } else {
        const changes = detectChanges(baseData.event, data.event);
        
        if (changes.length > 0) {
          changedEvents.push({
            screenName: data.screenName,
            screenDisplayName: data.screenDisplayName,
            oldEvent: baseData.event,
            newEvent: data.event,
            changes
          });
        } else {
          unchangedEvents.push({
            screenName: data.screenName,
            screenDisplayName: data.screenDisplayName,
            event: data.event
          });
        }
        
        if (data.event.status === 'pending') {
          pendingEvents.push({ screenName: data.screenName, screenDisplayName: data.screenDisplayName, event: data.event });
        } else if (data.event.status === 'in_progress') {
          inProgressEvents.push({ screenName: data.screenName, screenDisplayName: data.screenDisplayName, event: data.event });
        } else if (data.event.status === 'resolved') {
          resolvedEvents.push({ screenName: data.screenName, screenDisplayName: data.screenDisplayName, event: data.event });
        } else if (data.event.status === 'rejected') {
          rejectedEvents.push({ screenName: data.screenName, screenDisplayName: data.screenDisplayName, event: data.event });
        }
      }
    });
    
    baseEventsMap.forEach((data, key) => {
      if (!targetEventsMap.has(key)) {
        removedEvents.push({
          screenName: data.screenName,
          screenDisplayName: data.screenDisplayName,
          event: data.event
        });
      }
    });
    
    const addedScreens: any[] = [];
    const removedScreens: any[] = [];
    
    const baseScreensMap = new Map<string, Screen>();
    const targetScreensMap = new Map<string, Screen>();
    
    baseVersion.screens.forEach(screen => {
      baseScreensMap.set(screen.name, screen);
    });
    
    targetVersion.screens.forEach(screen => {
      targetScreensMap.set(screen.name, screen);
    });
    
    targetScreensMap.forEach((screen, name) => {
      const baseScreen = baseScreensMap.get(name);
      
      if (!baseScreen) {
        addedScreens.push({ screen });
      } else if (baseScreen.imageUrl !== screen.imageUrl) {
        // screen changed
      }
    });
    
    baseScreensMap.forEach((screen, name) => {
      if (!targetScreensMap.has(name)) {
        removedScreens.push({ screen });
      }
    });
    
    return {
      baseVersionId,
      targetVersionId,
      baseVersionName: `${baseVersion.version} (${baseVersion.name})`,
      targetVersionName: `${targetVersion.version} (${targetVersion.name})`,
      addedEvents,
      removedEvents,
      changedEvents,
      unchangedEvents,
      addedScreens,
      removedScreens,
      changedScreens: [],
      statusChanges: [],
      eventsByStatus: {
        pendingEvents,
        inProgressEvents,
        resolvedEvents,
        rejectedEvents
      },
      stats: {
        totalAdded: addedEvents.length,
        totalRemoved: removedEvents.length,
        totalChanged: changedEvents.length,
        totalUnchanged: unchangedEvents.length,
        totalPending: pendingEvents.length,
        totalInProgress: inProgressEvents.length,
        totalResolved: resolvedEvents.length,
        totalRejected: rejectedEvents.length
      }
    };
  }
});

// Create the store with slices
// Using spread operator to properly combine all slice functions
export const useStore = create<AppStore>()(
  persist(
    (set, get, api) => {
      // Create each slice with the zustand set/get/api
      const authSlice = createAuthSlice(set, get, api);
      const firebaseSlice = createFirebaseSlice(set, get, api);
      const appSlice = createAppSlice(set, get, api);
      const eventsSlice = createEventsSlice(set, get, api);
      const canvasSlice = createCanvasSlice(set, get, api);
      const filtersSlice = createFiltersSlice(set, get, api);
      const historySlice = createHistorySlice(set, get, api);
      const uiSlice = createUISlice(set, get, api);
      const additional = additionalStore(set, get, api);
      
      // Merge all slices together
      return {
        ...authSlice,
        ...firebaseSlice,
        ...appSlice,
        ...eventsSlice,
        ...canvasSlice,
        ...filtersSlice,
        ...historySlice,
        ...uiSlice,
        ...additional
      };
    },
    {
      name: 'ggb-analytics-filters',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ 
        eventFilters: state.eventFilters,
        apps: state.apps,
        currentAppId: state.currentAppId,
        currentVersionId: state.currentVersionId,
        currentScreenId: state.currentScreenId
      }),
    }
  )
);

// Expose store to window for tour actions
if (typeof window !== 'undefined') {
  (window as any).__STORE__ = useStore;
}
